import { useEffect, useRef } from 'react';
import { custom } from 'devextreme/ui/dialog';
import { BUILD_VERSION, formatBuildStampDisplay } from '../utils/appBuildStamp';

const POLL_MS = 5 * 60 * 1000;

function versionJsonUrl() {
  const base = process.env.PUBLIC_URL || '';
  if (!base) return '/version.json';
  return `${base.replace(/\/?$/, '')}/version.json`;
}

async function fetchRemoteVersion() {
  const res = await fetch(versionJsonUrl(), {
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data && typeof data.version === 'string' ? data.version : null;
}

function storageDismissKey(remote) {
  return `releasePromptDismissed:${remote}`;
}

export default function ReleaseVersionWatcher() {
  const prompting = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const timerRef = { id: null };

    const maybePrompt = async () => {
      if (cancelled || prompting.current) return;
      let remote;
      try {
        remote = await fetchRemoteVersion();
      } catch {
        return;
      }
      if (cancelled || remote == null || remote === BUILD_VERSION) return;
      try {
        if (sessionStorage.getItem(storageDismissKey(remote)) === '1') return;
      } catch {
        /* ignore */
      }

      prompting.current = true;
      try {
        await custom({
          title: 'Yeni sürüm',
          messageHtml:
            '<p>Portala yeni bir sürüm yüklendi. Sayfayı yenilediğinizde güncel sürüm ve önbellek temizliği uygulanır.</p>' +
            `<p style="opacity:.75;font-size:12px;margin-top:8px">Bu oturum: ${formatBuildStampDisplay(BUILD_VERSION)} · Sunucu: ${formatBuildStampDisplay(remote)}</p>`,
          showTitle: true,
          dragEnabled: true,
          buttons: [
            {
              text: 'Yenile',
              type: 'default',
              onClick: () => {
                window.location.reload();
                return true;
              }
            },
            {
              text: 'Daha sonra',
              onClick: () => {
                try {
                  sessionStorage.setItem(storageDismissKey(remote), '1');
                } catch {
                  /* ignore */
                }
                return true;
              }
            }
          ]
        });
      } finally {
        prompting.current = false;
      }
    };

    const schedule = () => {
      if (timerRef.id) clearInterval(timerRef.id);
      timerRef.id = setInterval(maybePrompt, POLL_MS);
    };

    maybePrompt();
    schedule();

    const onVisible = () => {
      if (document.visibilityState === 'visible') maybePrompt();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', maybePrompt);

    return () => {
      cancelled = true;
      if (timerRef.id) clearInterval(timerRef.id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', maybePrompt);
    };
  }, []);

  return null;
}
